import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowRight, Scissors } from "lucide-react";
import { useEffect } from "react";

const DEFAULT_HERO = "https://images.unsplash.com/photo-1578353022142-09264fd64295?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function PortalLogin() {
  const [orderNumber, setOrderNumber] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [hero, setHero] = useState(null);
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [badge, setBadge] = useState("Custom Shop");
  const [heading, setHeading] = useState("Welcome to the Wellsmade Co. Design Studio");
  const [subtext, setSubtext] = useState("Spots open every Monday at 9 AM ET. Limited spaces.");
  const { loginPortal } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/portal/branding`).then(({ data }) => {
      setHero(data?.hero ? `${API}/portal/hero?v=${data.hero}` : DEFAULT_HERO);
      if (data?.badge) setBadge(data.badge);
      if (data?.heading) setHeading(data.heading);
      if (data?.subtext) setSubtext(data.subtext);
    }).catch(() => setHero(DEFAULT_HERO));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/portal/login`, {
        order_number: orderNumber.trim().toUpperCase(),
        access_code: accessCode,
      });
      loginPortal(data.token);
      toast.success("Welcome to your design studio");
      navigate("/studio");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F8F6F0]">
      <div className="relative hidden lg:block overflow-hidden bg-[#161616]">
        {hero && (
          <img
            src={hero}
            alt="Atelier"
            onLoad={() => setHeroLoaded(true)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${heroLoaded ? "opacity-100" : "opacity-0"}`}
          />
        )}
        <div className="absolute inset-0 bg-[#161616]/45" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-[#F8F6F0]">
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em]">
            <Scissors className="h-4 w-4" /> {badge}
          </div>
          <div>
            <h1 className="font-serif text-5xl leading-[1.05] max-w-md">
              {heading}
            </h1>
            <p className="mt-5 text-[#F8F6F0]/80 max-w-sm text-base leading-relaxed">
              {subtext}
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-[#C85A32] mb-8">
            <Scissors className="h-4 w-4" /> Craft Studio
          </div>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#8C867B]">Customer Access</p>
          <h2 className="font-serif text-3xl mt-2 text-[#1A1918]">Enter your studio</h2>
          <p className="text-[#5C5852] mt-2 text-sm">Use the order number and access code from your confirmation.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#5C5852]">Order Number</Label>
              <Input
                data-testid="login-order-number-input"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="#1024"
                required
                className="h-12 bg-white border-[#C8C2AC] focus-visible:ring-[#C85A32]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest text-[#5C5852]">Access Code</Label>
              <Input
                data-testid="login-access-code-input"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                placeholder="A1B2C3"
                required
                className="h-12 bg-white border-[#C8C2AC] font-mono tracking-widest uppercase focus-visible:ring-[#C85A32]"
              />
            </div>
            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-[#C85A32] hover:bg-[#B04B26] text-[#F8F6F0] active:scale-[0.98] transition-transform duration-100"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Enter Studio <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
